import { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { ExternalLink, Info, ArrowUpDown, ArrowUp, ArrowDown, Globe, ShoppingCart, Check } from 'lucide-react';
import { translateCategories } from '../../utils/translations';
import { useLanguage } from '../../context/LanguageContext';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { useCart } from '../../context/CartContext';
import { toast } from 'sonner';

// Country to flag - using flag CDN images
const getCountryFlag = (country) => {
  if (!country) return null;
  const countryUpper = country.toUpperCase().trim();
  const countryCodes = {
    'SPAIN': 'es', 'SPAIN (SPANISH)': 'es', 'ESPAÑA': 'es',
    'FRANCE': 'fr', 'FRANCE (FRENCH)': 'fr', 'FRANCIA': 'fr',
    'GERMANY': 'de', 'GERMANY (GERMAN)': 'de', 'ALEMANIA': 'de',
    'ITALY': 'it', 'ITALY (ITALIAN)': 'it', 'ITALIA': 'it',
    'PORTUGAL': 'pt', 'PORTUGAL (PORTUGUESE)': 'pt',
    'UK': 'gb', 'UNITED KINGDOM': 'gb', 'REINO UNIDO': 'gb', 'GB': 'gb', 'GREAT BRITAIN': 'gb',
    'USA': 'us', 'UNITED STATES': 'us', 'ESTADOS UNIDOS': 'us', 'US': 'us',
    'NETHERLANDS': 'nl', 'HOLANDA': 'nl', 'PAISES BAJOS': 'nl',
    'BELGIUM': 'be', 'BÉLGICA': 'be', 'BELGICA': 'be',
    'AUSTRIA': 'at',
    'SWITZERLAND': 'ch', 'SUIZA': 'ch',
    'POLAND': 'pl', 'POLONIA': 'pl',
    'CZECH REPUBLIC': 'cz', 'CZECHIA': 'cz', 'REPÚBLICA CHECA': 'cz',
    'SWEDEN': 'se', 'SUECIA': 'se',
    'NORWAY': 'no', 'NORUEGA': 'no',
    'DENMARK': 'dk', 'DINAMARCA': 'dk',
    'FINLAND': 'fi', 'FINLANDIA': 'fi',
    'IRELAND': 'ie', 'IRLANDA': 'ie',
    'GREECE': 'gr', 'GRECIA': 'gr',
    'ROMANIA': 'ro', 'RUMANIA': 'ro',
    'HUNGARY': 'hu', 'HUNGRÍA': 'hu', 'HUNGRIA': 'hu',
    'BULGARIA': 'bg',
    'CROATIA': 'hr', 'CROACIA': 'hr',
    'SLOVAKIA': 'sk', 'ESLOVAQUIA': 'sk',
    'SLOVENIA': 'si', 'ESLOVENIA': 'si',
    'LITHUANIA': 'lt', 'LITUANIA': 'lt',
    'LATVIA': 'lv', 'LETONIA': 'lv',
    'ESTONIA': 'ee',
    'CYPRUS': 'cy', 'CHIPRE': 'cy',
    'MALTA': 'mt',
    'LUXEMBOURG': 'lu', 'LUXEMBURGO': 'lu',
    'BRAZIL': 'br', 'BRASIL': 'br',
    'MEXICO': 'mx', 'MÉXICO': 'mx',
    'ARGENTINA': 'ar',
    'CHILE': 'cl',
    'COLOMBIA': 'co',
    'PERU': 'pe', 'PERÚ': 'pe',
    'INDIA': 'in',
    'CHINA': 'cn',
    'JAPAN': 'jp', 'JAPÓN': 'jp', 'JAPON': 'jp',
    'SOUTH KOREA': 'kr', 'COREA DEL SUR': 'kr',
    'AUSTRALIA': 'au',
    'CANADA': 'ca', 'CANADÁ': 'ca',
    'RUSSIA': 'ru', 'RUSIA': 'ru',
    'TURKEY': 'tr', 'TURQUÍA': 'tr', 'TURQUIA': 'tr',
    'INDONESIA': 'id',
    'THAILAND': 'th', 'TAILANDIA': 'th',
    'VIETNAM': 'vn',
    'MALAYSIA': 'my', 'MALASIA': 'my',
    'SINGAPORE': 'sg', 'SINGAPUR': 'sg',
    'PHILIPPINES': 'ph', 'FILIPINAS': 'ph',
    'EGYPT': 'eg', 'EGIPTO': 'eg',
    'SOUTH AFRICA': 'za', 'SUDÁFRICA': 'za', 'SUDAFRICA': 'za',
    'ISRAEL': 'il',
    'UNITED ARAB EMIRATES': 'ae', 'UAE': 'ae', 'EMIRATOS ÁRABES': 'ae',
    'SAUDI ARABIA': 'sa', 'ARABIA SAUDITA': 'sa',
    'UKRAINE': 'ua', 'UCRANIA': 'ua',
    'GLOBAL': null,
    'INTERNATIONAL': null,
  };
  const code = countryCodes[countryUpper];
  return code ? `https://flagcdn.com/24x18/${code}.png` : null;
};

// Parse ALL link types from the field and return array of badges
const getLinkTypeBadges = (linkType) => {
  if (!linkType || linkType === 'No' || linkType.trim() === '') return [];
  const badges = [];
  const lower = linkType.toLowerCase();
  const parts = lower.split(',').map(p => p.trim());

  // Track what we've added to avoid duplicates
  let hasDoFollow = false;
  let hasNoFollow = false;
  let hasSponsored = false;
  let hasUGC = false;
  let hasPermanente = false;

  for (const part of parts) {
    // DoFollow: "follow", "dofollow", "do-follow" (but not "no-follow" or "nofollow")
    if ((part === 'follow' || part === 'dofollow' || part === 'do-follow') && !hasDoFollow) {
      badges.push({ label: 'DoFollow', color: 'bg-green-100 text-green-700' });
      hasDoFollow = true;
    }
    // NoFollow: "no-follow", "nofollow"
    if ((part === 'no-follow' || part === 'nofollow') && !hasNoFollow) {
      badges.push({ label: 'NoFollow', color: 'bg-slate-100 text-slate-600' });
      hasNoFollow = true;
    }
    // Sponsored
    if (part === 'sponsored' && !hasSponsored) {
      badges.push({ label: 'Sponsored', color: 'bg-amber-100 text-amber-700' });
      hasSponsored = true;
    }
    // UGC
    if (part === 'ugc' && !hasUGC) {
      badges.push({ label: 'UGC', color: 'bg-purple-100 text-purple-700' });
      hasUGC = true;
    }
    // Permanente
    if ((part === 'permanente' || part === 'permanent') && !hasPermanente) {
      badges.push({ label: 'Permanente', color: 'bg-blue-100 text-blue-700' });
      hasPermanente = true;
    }
  }

  // If no recognized badges but linkType exists, show the original value
  if (badges.length === 0 && linkType.trim()) {
    badges.push({ label: linkType, color: 'bg-gray-100 text-gray-700' });
  }

  return badges;
};

export const ProductTable = ({ products, texts, onProductClick, sortField: externalSortField, sortOrder: externalSortOrder, onSort: externalOnSort, selectedProducts, onSelectProduct, onSelectAll }) => {
  const { language } = useLanguage();
  const { tx } = useAdminTranslation();
  const { addToCart, isInCart } = useCart();
  
  // Use external sort if provided, otherwise use local state
  const [localSortField, setLocalSortField] = useState('');
  const [localSortOrder, setLocalSortOrder] = useState('desc');
  
  const sortField = externalSortField !== undefined ? externalSortField : localSortField;
  const sortOrder = externalSortOrder !== undefined ? externalSortOrder : localSortOrder;

  const t = texts || {
    tableUrl: 'URL',
    tableCountry: 'País',
    tablePrice: 'Precio',
    tablePriceSensitive: 'Precio Sensible',
    tableDr: 'DR',
    tableTraffic: 'Tráfico',
    tableSiteType: 'Tipo Medio',
    tableLinkType: 'Tipo Enlace',
    tableCategory: 'Categoría',
    tableMoreInfo: 'Info',
    tableCart: 'Carrito',
  };

  const handleAddToCart = (e, product) => {
    e.stopPropagation();
    if (isInCart(product.id)) {
      toast.info(tx(`"${product.website}" ya está en el carrito`, `"${product.website}" is already in cart`));
      return;
    }
    addToCart(product, 'normal');
    toast.success(tx(`"${product.website}" añadido al carrito`, `"${product.website}" added to cart`));
  };

  // Handle table sort - use external handler if provided
  const handleSort = (field) => {
    if (externalOnSort) {
      externalOnSort(field);
    } else {
      if (localSortField === field) {
        setLocalSortOrder(localSortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setLocalSortField(field);
        setLocalSortOrder('desc');
      }
    }
  };

  // Get sorted products for table view (only use local sorting if no external sort)
  const getSortedProducts = () => {
    // If using external sort (server-side), just return products as-is
    if (externalOnSort) return products;
    if (!sortField) return products;

    return [...products].sort((a, b) => {
      let aVal, bVal;
      
      if (sortField === 'price') {
        aVal = a.pvp || a.price || 0;
        bVal = b.pvp || b.price || 0;
      } else if (sortField === 'dr') {
        aVal = a.dr || 0;
        bVal = b.dr || 0;
      } else if (sortField === 'traffic') {
        aVal = a.traffic || a.organic_traffic || 0;
        bVal = b.traffic || b.organic_traffic || 0;
      } else {
        aVal = a[sortField] || 0;
        bVal = b[sortField] || 0;
      }
      
      if (sortOrder === 'asc') {
        return aVal - bVal;
      }
      return bVal - aVal;
    });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-md" data-testid="product-table" style={{maxHeight: '70vh', overflow: 'auto'}}>
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-0 z-30 bg-indigo-100" style={{position: 'sticky', top: 0}}>
          <tr className="border-b-2 border-indigo-300">
            <th className="font-bold text-indigo-900 whitespace-nowrap px-2 py-3 text-center bg-indigo-100 w-8">
              {onSelectAll && (
                <Checkbox
                  checked={selectedProducts && products.length > 0 && selectedProducts.size === products.length}
                  onCheckedChange={onSelectAll}
                  className="h-3.5 w-3.5"
                />
              )}
            </th>
            <th className="font-bold text-indigo-900 whitespace-nowrap px-2 py-3 text-left bg-indigo-100">{t.tableUrl}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left hidden sm:table-cell bg-indigo-100">{t.tableCountry}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('price')}>
              <div className="flex items-center gap-1">
                {t.tablePrice}
                {sortField === 'price' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-amber-700 whitespace-nowrap px-2 py-3 text-left hidden md:table-cell bg-indigo-100">{t.tablePriceSensitive}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('dr')}>
              <div className="flex items-center gap-1">
                {t.tableDr}
                {sortField === 'dr' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left cursor-pointer hover:bg-indigo-200 bg-indigo-100" onClick={() => handleSort('traffic')}>
              <div className="flex items-center gap-1">
                {t.tableTraffic}
                {sortField === 'traffic' ? (sortOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
              </div>
            </th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left hidden md:table-cell bg-indigo-100">{t.tableSiteType}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-left hidden md:table-cell bg-indigo-100">{t.tableLinkType}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-center hidden md:table-cell bg-indigo-100">{tx('Sens.', 'Sens.')}</th>
            <th className="font-semibold text-slate-700 whitespace-nowrap px-2 py-3 text-center hidden lg:table-cell bg-indigo-100">Tag</th>
            <th className="text-center font-semibold text-slate-700 whitespace-nowrap px-2 py-3 bg-indigo-100">{t.tableCart || 'Carrito'}</th>
            <th className="text-center font-semibold text-slate-700 whitespace-nowrap px-2 py-3 bg-indigo-100">{t.tableMoreInfo}</th>
          </tr>
        </thead>
        <tbody>
          {getSortedProducts().map((product, index) => (
            <tr 
              key={product.id} 
              className={`hover:bg-indigo-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
              data-testid={`product-row-${index}`}
            >
              <td className="px-2 py-1.5 text-center">
                {onSelectProduct && (
                  <Checkbox
                    checked={selectedProducts ? selectedProducts.has(product.id) : false}
                    onCheckedChange={() => onSelectProduct(product.id)}
                    className="h-3.5 w-3.5"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </td>
              <td className="px-2 py-1.5">
                <a 
                  href={(() => {
                    const baseUrl = product.website?.startsWith('http') ? product.website : `https://${product.website}`;
                    const separator = baseUrl.includes('?') ? '&' : '?';
                    return `${baseUrl}${separator}utm_source=purolink&utm_medium=catalog&utm_campaign=linkbuilding`;
                  })()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium flex items-center gap-1 text-xs"
                  title={product.website}
                  data-testid={`product-url-${index}`}
                >
                  <span className="truncate max-w-[120px] sm:max-w-[150px] lg:max-w-[200px]">
                    {product.website?.replace(/^https?:\/\//, '').replace(/\/$/, '') || '-'}
                  </span>
                  <ExternalLink className="h-3 w-3 opacity-60 shrink-0" />
                </a>
              </td>
              <td className="px-2 py-1.5 hidden sm:table-cell">
                {getCountryFlag(product.country) ? (
                  <img 
                    src={getCountryFlag(product.country)} 
                    alt={product.country}
                    title={product.country}
                    className="h-3 w-5 object-cover rounded-sm"
                  />
                ) : (
                  <span className="text-xs text-slate-400">{product.country?.substring(0,3) || '-'}</span>
                )}
              </td>
              <td className="px-2 py-1.5">
                <span className="font-bold text-emerald-600 bg-emerald-50 px-1 py-0.5 rounded text-xs">€{product.pvp || product.price || 0}</span>
              </td>
              <td className="px-2 py-1.5 hidden md:table-cell">
                {(product.premium_price || product.premium_sale_price || product.price_sensitive) && (product.premium_price || product.premium_sale_price || product.price_sensitive) > 0 ? (
                  <span className="font-bold text-amber-600 bg-amber-50 px-1 py-0.5 rounded text-xs">€{product.premium_price || product.premium_sale_price || product.price_sensitive}</span>
                ) : (
                  <span className="text-slate-300 text-xs">—</span>
                )}
              </td>
              <td className="px-2 py-1.5">
                <span className="font-bold text-violet-600 bg-violet-50 px-1 py-0.5 rounded text-xs">{product.dr || '-'}</span>
              </td>
              <td className="px-2 py-1.5">
                <span className="font-medium text-sky-600 text-xs">{(product.traffic || product.organic_traffic || 0).toLocaleString()}</span>
              </td>
              <td className="px-2 py-1.5 hidden md:table-cell">
                {product.site_type ? (
                  <Badge className={`text-[10px] px-1 py-0 ${product.site_type?.toLowerCase().includes('periodico') ? 'bg-purple-100 text-purple-700' : 'bg-teal-100 text-teal-700'}`}>
                    {product.site_type}
                  </Badge>
                ) : <span className="text-slate-300">-</span>}
              </td>
              <td className="px-2 py-1.5 hidden md:table-cell">
                <div className="flex flex-wrap gap-0.5">
                  {getLinkTypeBadges(product.link_type).map((badge, idx) => (
                    <Badge key={idx} className={`text-[10px] px-1 py-0 ${badge.color}`}>
                      {badge.label}
                    </Badge>
                  ))}
                  {getLinkTypeBadges(product.link_type).length === 0 && <span className="text-slate-300">—</span>}
                </div>
              </td>
              <td className="px-2 py-1.5 text-center hidden md:table-cell">
                {(() => {
                  const acceptsSensitive = !!(product.casino || product.cbd || product.gambling || product.cryptocurrency || product.sex || product.erotic || product.vpn || product.tarot || product.porn || product.cannabis);
                  return (
                    <Badge
                      variant={acceptsSensitive ? 'destructive' : 'outline'}
                      className={`text-[10px] px-1.5 ${acceptsSensitive ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}
                    >
                      {acceptsSensitive ? 'Sí' : 'No'}
                    </Badge>
                  );
                })()}
              </td>
              <td className="px-2 py-1.5 text-center hidden lg:table-cell">
                {product.tag_label && product.tag_label.toLowerCase() === 'yes' ? (
                  <Badge className="text-[10px] px-1.5 bg-red-100 text-red-700 border-red-200">
                    Yes
                  </Badge>
                ) : (
                  <Badge className="text-[10px] px-1.5 bg-green-100 text-green-700 border-green-200">
                    No
                  </Badge>
                )}
              </td>
              <td className="px-2 py-1.5 text-center">
                <Button 
                  variant={isInCart(product.id) ? "secondary" : "ghost"}
                  size="sm" 
                  onClick={(e) => handleAddToCart(e, product)}
                  disabled={isInCart(product.id)}
                  className={`h-6 w-6 p-0 rounded-full ${isInCart(product.id) ? 'bg-green-100 text-green-600' : 'bg-primary/10 text-primary hover:bg-primary/20'}`}
                  title={isInCart(product.id) ? 'En el carrito' : 'Añadir al carrito'}
                >
                  {isInCart(product.id) ? <Check className="h-3 w-3" /> : <ShoppingCart className="h-3 w-3" />}
                </Button>
              </td>
              <td className="px-2 py-1.5 text-center">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => onProductClick(product)}
                  className="h-6 w-6 p-0 rounded-full bg-indigo-100 text-indigo-600 hover:bg-indigo-200"
                  data-testid={`product-info-${index}`}
                >
                  <Info className="h-3 w-3" />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ProductTable;
